/* External */
import {
  memo,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Utils */
import { useUserAccount } from '~/providers/UserProvider';
import { useTermsOfServiceInfo } from '~/providers/TermsOfServiceProvider';

import { useOnBackPress } from '~/utils/keyDownEvents';
import { noOp, appConsoleLog } from '~/utils/functions';
import { TAPPP_SDK_URL, TAPPP_USE_GUIDEID } from '~/utils/env';
import { TAPPP_CONFIG_DATA } from '~/utils/constants';
import { GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';
import { isTapppPanelEnabledForUser } from '../../../utils/profiles';

// Styles
import './TapppPanel.scss';

const {
  env, broadcasterName, deviceType, sdkScriptId,
} = TAPPP_CONFIG_DATA;

const panelSetting = { width: '100', widthunit: '%' };

function tapppLibraryStop() {
  try {
    window.TapppPanelLibrary.stop();
    appConsoleLog('[TAPPP] TapppPanel: Called TapppPanelLibrary STOP');
  } catch (error) {
    console.error('TapppPanel>tapppLibraryStop', error);
  }
}

function TapppPanel({
  hide = noOp,
  show = false,
  playerCommonTagEvent = noOp,
  schedule = undefined,
  content = undefined,
  handleEnterPressTapppPanel = noOp,
} = {}) {
  useOnBackPress({ listener: hide, when: show });

  const divRef = useRef();
  const profileId = JSON.parse(localStorage.getItem('profile'))?.id;
  const profileName = JSON.parse(localStorage.getItem('profile'))?.name;
  const sessionToken = JSON.parse(localStorage.getItem('accessTokenData'))?.idToken;
  const { uid: userId, iso2Code } = useUserAccount() || {};
  const { shouldAccept, isLoadingShouldAccept } = useTermsOfServiceInfo();
  const contentId = TAPPP_USE_GUIDEID
    ? (schedule?.guideId || content?.guideId)
    : (schedule?.vrioAssetId || content?.vrioAssetId);
  const hasBetsInteractivity = schedule?.interactivities?.bets || content?.interactivities?.bets;

  const onTapppActionEvent = useCallback((event) => {
    switch (event.actionName) {
      case 'CLOSE_PANEL': {
        hide();
        appConsoleLog('[TAPPP] TapppPanel: Called CLOSE_PANEL');
        break;
      }
      case 'OPEN': {
        handleEnterPressTapppPanel();
        appConsoleLog('[TAPPP] TapppPanel: Called OPEN');
        break;
      }
      default: {
        break;
      }
    }
  }, [hide, handleEnterPressTapppPanel]);

  const shouldActivateTapppPanelToUser = isTapppPanelEnabledForUser(iso2Code, userId);
  const isProfileEnableTappp = JSON.parse(
    localStorage.getItem('profile'),
  )?.interactivities?.isBetAllowed || false;

  const shouldTapppPanelInjected = useMemo(() => shouldActivateTapppPanelToUser
    && isProfileEnableTappp && hasBetsInteractivity && !shouldAccept
    && !isLoadingShouldAccept, [
    shouldActivateTapppPanelToUser,
    isProfileEnableTappp,
    hasBetsInteractivity,
    shouldAccept,
    isLoadingShouldAccept,
  ]);

  const injectTapppPanel = useCallback(() => {
    if (shouldTapppPanelInjected) {
      const scriptElement = document.createElement('script');
      scriptElement.src = TAPPP_SDK_URL;
      scriptElement.id = sdkScriptId;

      document.head.appendChild(scriptElement);

      scriptElement.addEventListener('load', () => {
        const tapppPanelCreate = window.createPanel(divRef?.current);
        window.TapppPanelLibrary.initPanel({
          [tapppPanelCreate?.User?.USER_ID]: userId,
          [tapppPanelCreate?.User?.USER_PROFILE]: {
            profileId,
            profileName,
          },
          [tapppPanelCreate?.Sports?.CONTENT_ID]: contentId,
          [tapppPanelCreate?.User?.USER_AUTH_TOKEN]: sessionToken,
          [tapppPanelCreate?.Environment?.ENVIRONMENT]: env,
          [tapppPanelCreate?.Sports?.BROADCASTER_NAME]: broadcasterName,
          [tapppPanelCreate?.Sports?.DEVICE_TYPE]: deviceType,
        }, panelSetting); // To Initialize panel

        appConsoleLog('[TAPPP] TapppPanel: Init Pannel When Script Does Not Exist', {
          [tapppPanelCreate?.User?.USER_ID]: userId,
          [tapppPanelCreate?.User?.USER_PROFILE]: {
            profileId,
            profileName,
          },
          [tapppPanelCreate?.Sports?.CONTENT_ID]: contentId,
          [tapppPanelCreate?.User?.USER_AUTH_TOKEN]: sessionToken,
          [tapppPanelCreate?.Environment?.ENVIRONMENT]: env,
          [tapppPanelCreate?.Sports?.BROADCASTER_NAME]: broadcasterName,
          [tapppPanelCreate?.Sports?.DEVICE_TYPE]: deviceType,
        });

        window.TapppPanelLibrary.subscribe(
          window.TapppPanelLibrary.EVENTS.RMG,
          window.TapppPanelLibrary.EVENTS.ACTION_EVENT,
          onTapppActionEvent,
        );
      });
    }
  }, [
    sessionToken,
    profileName,
    profileId,
    userId,
    contentId,
    onTapppActionEvent,
    shouldTapppPanelInjected,
  ]);

  useEffect(() => {
    injectTapppPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount
  }, []);

  const startStopTapppPanel = useCallback((showTapppPanel) => {
    if (window.TapppPanelLibrary && window.createPanel && hasBetsInteractivity) {
      if (showTapppPanel) {
        window.TapppPanelLibrary.start();
        playerCommonTagEvent({
          subcategory: GTM_SUBCATEGORIES.PLAYER.LIVE,
          eventName: GTM_EVENT_NAMES.PLAYER.TAPPP_PANEL,
        });
        appConsoleLog('[TAPPP] TapppPanel: Called TapppPanelLibrary START');
      } else {
        tapppLibraryStop();
      }
    }
  }, [hasBetsInteractivity, playerCommonTagEvent]);

  useEffect(() => {
    startStopTapppPanel(show);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when show changes
  }, [show]);

  return (
    <div className="dtv-html5-c-tappp-panel">
      <div id="tappp-panel" ref={divRef} />
    </div>
  );
}

TapppPanel.propTypes = {
  show: PropTypes.bool,
  hide: PropTypes.func,
  playerCommonTagEvent: PropTypes.func,
  // Metadata
  schedule: PropTypes.shape({}),
  content: PropTypes.shape({}),
  handleEnterPressTapppPanel: PropTypes.func,
};

export default memo(withFocusable()(memo(TapppPanel)));
